import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { ISimejiItem } from '@template/Simeji/simejiStatus.interface';
import { STATUS_TYPE, STATUS_TYPE_VALUE } from '@template/Simeji/simejiStatus';

// ─── 상태별 CSS 애니메이션 (이미지 전용, 기존 DrawBody 와 keyframe 이름 충돌 방지) ───

const imageTypeMap: Record<string, ReturnType<typeof css>> = {
  [STATUS_TYPE.DEFAULT]: css`
    animation: 1.5s ease-in-out infinite alternate imgBreath;

    @keyframes imgBreath {
      0% {
        transform: translateY(0);
      }
      100% {
        transform: translateY(-2px);
      }
    }
  `,

  [STATUS_TYPE.WALK]: css`
    animation: 0.35s ease-in-out infinite alternate imgWalkBounce;

    @keyframes imgWalkBounce {
      0% {
        transform: translateY(0);
      }
      100% {
        transform: translateY(-3px);
      }
    }
  `,

  [STATUS_TYPE.SLEEP]: css`
    transform-origin: bottom center;
    animation: 2s ease-in-out infinite alternate imgSleep;

    @keyframes imgSleep {
      0% {
        transform: rotate(0deg) translateY(0);
      }
      50% {
        transform: rotate(-5deg) translateY(2px);
      }
      100% {
        transform: rotate(5deg) translateY(2px);
      }
    }
  `,

  [STATUS_TYPE.EAT]: css`
    animation: 0.2s linear infinite alternate imgEat;

    @keyframes imgEat {
      0% {
        transform: translateY(0);
      }
      100% {
        transform: translateY(-2px);
      }
    }
  `,

  [STATUS_TYPE.CLICK]: css`
    animation: 0.15s ease-out forwards imgBounce;

    @keyframes imgBounce {
      0% {
        transform: scale(1);
      }
      50% {
        transform: scale(0.9);
      }
      100% {
        transform: scale(1);
      }
    }
  `,

  [STATUS_TYPE.MOVE]: css`
    transform-origin: top center;
    animation: 0.5s ease-in-out infinite alternate imgShake;

    @keyframes imgShake {
      0% {
        transform: rotate(5deg);
      }
      100% {
        transform: rotate(-5deg);
      }
    }
  `,

  [STATUS_TYPE.CLIMB]: css``,
  [STATUS_TYPE.PENDING]: css``,
};

// ─── Styled Components ─────────────────────────────────────────────────

const ImageBodyBox = styled.div<{
  $customType: STATUS_TYPE_VALUE;
  $shouldMirror: boolean;
  $imageWidth: number;
}>`
  position: relative;
  width: ${({ $imageWidth }) => $imageWidth}px;
  display: flex;
  justify-content: center;
  align-items: flex-end;

  ${({ $customType }) => imageTypeMap[$customType] || ''};

  img {
    width: 100%;
    height: auto;
    display: block;
    user-select: none;
    pointer-events: none;
    image-rendering: pixelated;
    ${({ $shouldMirror }) =>
    $shouldMirror &&
    css`
        transform: scaleX(-1);
      `}
  }
`;

// ─── Component ─────────────────────────────────────────────────────────

type Props = {
  data: ISimejiItem;
  dir?: number;
  type?: STATUS_TYPE_VALUE;
};

const DrawImageBody: React.FC<Props> = React.memo(
  ({ data, dir = 0, type = STATUS_TYPE.DEFAULT }) => {
    const assets = data.imageAssets;
    const imageWidth = data.imageWidth || 100;

    // ── 2프레임 교차 토글 ──
    const [frameToggle, setFrameToggle] = useState(false);
    const frameRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
      if (!assets) return;

      const isWalking = type === STATUS_TYPE.WALK;
      const isEating = type === STATUS_TYPE.EAT;

      const hasWalkFrame2 =
        dir >= 0
          ? !!assets.rightWalk2
          : !!(assets.leftWalk2 || assets.rightWalk2);
      const hasEatFrame2 = !!assets.eat2;

      const shouldAnimate =
        (isWalking && hasWalkFrame2) || (isEating && hasEatFrame2);

      if (shouldAnimate) {
        const interval = isEating ? 200 : data.speed || 500;
        frameRef.current = setInterval(() => {
          setFrameToggle((prev) => !prev);
        }, interval);
      } else {
        setFrameToggle(false);
      }

      return () => {
        if (frameRef.current) {
          clearInterval(frameRef.current);
          frameRef.current = null;
        }
      };
    }, [type, dir, assets, data.speed]);

    // ── 현재 상태 + 방향에 맞는 이미지 URL 반환 ──
    const getImageSrc = useCallback((): string => {
      if (!assets) return '';

      switch (type) {
        case STATUS_TYPE.WALK: {
          if (dir >= 0) {
            // 오른쪽 (또는 중립)
            if (frameToggle && assets.rightWalk2) return assets.rightWalk2;
            return assets.right;
          }
          // 왼쪽
          if (frameToggle) {
            if (assets.leftWalk2) return assets.leftWalk2;
            if (assets.rightWalk2) return assets.rightWalk2; // → mirror 처리
          }
          return assets.left || assets.right; // left 없으면 right → mirror
        }

        case STATUS_TYPE.MOVE:
          return assets.lifted;

        case STATUS_TYPE.EAT: {
          if (frameToggle && assets.eat2) return assets.eat2;
          return assets.eat;
        }

        case STATUS_TYPE.SLEEP:
          return assets.sleep || assets.front;

        case STATUS_TYPE.CLICK:
          return assets.click || assets.front;

        case STATUS_TYPE.DEFAULT:
        default:
          return assets.front;
      }
    }, [type, dir, frameToggle, assets]);

    // ── left 미등록 시 right 좌우반전 여부 판별 ──
    const getShouldMirror = useCallback((): boolean => {
      if (!assets) return false;
      if (type !== STATUS_TYPE.WALK || dir !== -1) return false;

      // 왼쪽 걷기인데 전용 이미지가 없으면 mirror
      if (frameToggle) {
        // 2프레임 상태
        if (!assets.leftWalk2 && assets.rightWalk2) return true;
        if (!assets.leftWalk2 && !assets.rightWalk2 && !assets.left)
          return true;
      }
      return !assets.left;
    }, [type, dir, frameToggle, assets]);

    // ── Render ──
    const imgSrc = getImageSrc();
    const shouldMirror = getShouldMirror();

    if (!assets || !imgSrc) return null;

    return (
      <ImageBodyBox
        $customType={type}
        $shouldMirror={shouldMirror}
        $imageWidth={imageWidth}
      >
        <img src={imgSrc} alt={data.title || 'simeji'} draggable={false} />
      </ImageBodyBox>
    );
  },
);

DrawImageBody.displayName = 'DrawImageBody';

export default DrawImageBody;
